import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, User, LogOut, Settings, Bookmark, Moon, Sun, Shield } from 'lucide-react';

export const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, settings, updateSettings, isAdmin } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Don't show header on homepage
    if (location.pathname === '/') {
        return null;
    }

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleDarkMode = () => {
        updateSettings({ ...settings, dark_mode: !settings.dark_mode });
    };

    return (
        <header className="border-b border-foreground/20">
            <div className="listovna-container">
                <nav className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link 
                        to="/" 
                        className="text-2xl italic hover:opacity-70 transition-opacity"
                        data-testid="logo-link"
                    >
                        Listovna
                    </Link>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-6">
                        <Link to="/povidky" className="nav-link" data-testid="nav-povidky">
                            Povídky
                        </Link>
                        <Link to="/autori" className="nav-link" data-testid="nav-autori">
                            Autoři
                        </Link>
                        
                        <button
                            onClick={toggleDarkMode}
                            className="p-2 hover:bg-secondary rounded-full transition-colors"
                            data-testid="dark-mode-toggle"
                            aria-label="Toggle dark mode"
                        >
                            {settings.dark_mode ? (
                                <Sun className="w-5 h-5" />
                            ) : (
                                <Moon className="w-5 h-5" />
                            )}
                        </button>

                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2 italic"
                                        data-testid="user-menu-trigger"
                                    >
                                        <User className="w-5 h-5" />
                                        <span>{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem asChild>
                                        <Link to="/ucet" className="flex items-center gap-2" data-testid="menu-account">
                                            <Settings className="w-4 h-4" />
                                            Můj účet
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/ulozene" className="flex items-center gap-2" data-testid="menu-bookmarks">
                                            <Bookmark className="w-4 h-4" />
                                            Uložené
                                        </Link>
                                    </DropdownMenuItem>
                                    {isAdmin() && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin" className="flex items-center gap-2" data-testid="menu-admin">
                                                    <Shield className="w-4 h-4" />
                                                    Administrace
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="flex items-center gap-2 text-destructive"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Odhlásit se
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/prihlaseni">
                                <Button variant="ghost" className="italic" data-testid="login-btn">
                                    Přihlásit se
                                </Button>
                            </Link>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden p-2"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? (
                            <X className="w-6 h-6" />
                        ) : (
                            <Menu className="w-6 h-6" />
                        )}
                    </button>
                </nav>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-6 border-t border-foreground/20 animate-fade-in">
                        <div className="flex flex-col gap-4">
                            <Link
                                to="/povidky"
                                className="text-xl italic py-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Povídky
                            </Link>
                            <Link
                                to="/autori"
                                className="text-xl italic py-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Autoři
                            </Link>
                            <div className="flex items-center gap-4 pt-4 border-t border-foreground/20">
                                <button
                                    onClick={toggleDarkMode}
                                    className="p-2 hover:bg-secondary rounded-full transition-colors"
                                    aria-label="Toggle dark mode"
                                >
                                    {settings.dark_mode ? (
                                        <Sun className="w-5 h-5" />
                                    ) : (
                                        <Moon className="w-5 h-5" />
                                    )}
                                </button>
                                {user ? (
                                    <div className="flex flex-col gap-2 flex-1">
                                        <Link 
                                            to="/ucet" 
                                            className="italic"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Můj účet
                                        </Link>
                                        {isAdmin() && (
                                            <Link 
                                                to="/admin" 
                                                className="italic"
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Administrace
                                            </Link>
                                        )}
                                        <button 
                                            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                            className="text-destructive text-left italic"
                                        >
                                            Odhlásit se
                                        </button>
                                    </div>
                                ) : (
                                    <Link 
                                        to="/prihlaseni"
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="italic"
                                    >
                                        Přihlásit se
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};
